import React, {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import './HeaderInfo.less';
import cl from 'classnames';
import LanguageSelect from '../../LanguageSelect';
import {ROUTES, buildRoute} from '../../../configs/routes';
import URL_MAP from '../../../constants/urls';
import HeaderAccordionMenu from './HeaderAccordionMenu';
import presentationRu from '../../../assets/files/mnj_presentation_ru.pdf';
import presentationEng from '../../../assets/files/mnj_presentation_eng.pdf';
import {ActionPagesUpdateState} from '../../../store/actions/actions-pages';
import {LanguageContext} from '../../../context/LanguageContext';
import {t} from '../../../services/i18nHandler';

const candidateLinks = [
  {
    title: 'nav.freeOnlineVideosession',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.FREE_VIDEOSESSIONS),
  },
  {
    title: 'nav.videoInterviewRecord',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.VIDEOINTERVIEW_RECORDING),
  },
  {
    title: 'nav.videoResumeRecord',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.VIDEORESUME_RECORDING),
  },
  {
    title: 'nav.portfolio',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.CANDIDATES_PORTFOLIO),
  },
  {
    title: 'nav.linkPlacement',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.LINKS),
  },
  {
    title: 'nav.resumeAndMails',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.TEXT_RESUME),
  },
  {
    title: 'nav.vacanciesBd',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.VACANCIES_DATA_BASE),
  },
  {
    title: 'nav.searchWork',
    link: buildRoute(URL_MAP.YOU_CANDIDATE, URL_MAP.WORK_SEARCHES),
  },
];
const employerLinks = [
  {
    title: 'nav.freeOnlineVideosession',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.FREE_VIDEOSESSIONS),
  },
  {
    title: 'nav.videoInterviewRecord',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.VIDEOINTERVIEW_RECORDING),
  },
  {
    title: 'nav.videoVacancyRecord',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.VIDEOVACANCY_RECORDING),
  },
  {
    title: 'nav.videoPollRecord',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.VIDEOPOOL_RECORDING),
  },
  {
    title: 'nav.linkPlacement',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.LINKS),
  },
  {
    title: 'nav.vacanciesAndVideoBusinessCard',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.TEXT_VACANCY),
  },
  {
    title: 'nav.resumeBd',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.RESUME_DATA_BASE),
  },
  {
    title: 'nav.inviteVideoInterview',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.CANDIDATE_SEARCHES),
  },
  {
    title: 'nav.candidatesFlow',
    link: buildRoute(URL_MAP.YOU_EMPLOYER, URL_MAP.CANDIDATES_FLOW),
  },
];
const agencyLinks = [
  {
    title: 'nav.freeOnlineVideosession',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.FREE_VIDEOSESSIONS),
  },
  {
    title: 'nav.videoInterviewRecord',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.VIDEOINTERVIEW_RECORDING),
  },
  {
    title: 'nav.videoVacancyRecord',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.VIDEOVACANCY_RECORDING),
  },
  {
    title: 'nav.videoPollRecord',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.VIDEOPOOL_RECORDING),
  },
  {
    title: 'nav.linkPlacement',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.LINKS),
  },
  {
    title: 'nav.vacanciesAndVideoBusinessCard',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.TEXT_VACANCY),
  },
  {
    title: 'nav.resumeBd',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.RESUME_DATA_BASE),
  },
  {
    title: 'nav.inviteVideoInterview',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.CANDIDATE_SEARCHES),
  },
  {
    title: 'nav.privatePage',
    link: buildRoute(URL_MAP.YOU_AGENCY, URL_MAP.PERSONAL_PAGE),
  },
];
const companyLinks = [
  {
    title: 'nav.howItWork',
    link: ROUTES.HOW_IT_WORK.route,
  },
  {
    title: 'nav.aboutCompany',
    link: ROUTES.ABOUT_COMPANY.route,
  },
  {
    title: 'nav.news',
    link: ROUTES.NEWS.route,
  },
  {
    title: 'nav.downloadPresentationRu',
    link: presentationRu,
    download: true,
  },
  {
    title: 'nav.downloadPresentationEn',
    link: presentationEng,
    download: true,
  },
];

const infoNavLinks = [
  {
    title: 'nav.tariffs',
    link: buildRoute(URL_MAP.PLANS),
  },
  {
    title: 'nav.contacts',
    link: ROUTES.CONTACTS.route,
  },
  {
    title: 'nav.technicalSupport',
    link: ROUTES.TECHNICAL_SUPPORT.route,
  },
  {
    title: 'nav.servicesTerms',
    link: ROUTES.SERVICE_TERMS.route,
  },
  {
    title: 'nav.safeMnj',
    link: ROUTES.SAFE_MNJ.route,
  },
  {
    title: 'nav.softwareRequirements',
    link: ROUTES.SOFTWARE_REQUIREMENTS.route,
  },
  {
    title: 'nav.documents',
    link: ROUTES.LEGAL_DOCUMENTS.route,
  },
  {
    title: 'nav.dataTerms',
    link: ROUTES.PROCESSING_POLICY.route,
  },
];

function HeaderInfo({
  hideInfoBar, user, cartData, loggedIn,
}) {
  const infoBarRef = useRef();

  const language = useContext(LanguageContext);

  const dispatch = useDispatch();

  const userId = useMemo(() => (user._id), [user]);

  const userCartData = cartData[userId] || [];

  const currentTariff = useMemo(() => (user.tariff || {}), [user]);

  const currentTariffDetails = useMemo(() => (currentTariff.details || {}), [currentTariff]);

  const tariffName = useMemo(() => {
    const tName = currentTariffDetails.name || {};
    return tName[language] || '';
  }, [currentTariffDetails, language]);

  // TODO: highligh active link;
  // const isActive = useMemo(() => (route = '') => {
  //   const pathname = router.location.pathname;
  //   const urlArr = pathname.split('/');
  //   const currentPath = urlArr[1];
  //   return currentPath.includes(route);
  // }, [router]);
  const [xDown, setXDown] = useState(null);

  const handleTouchStart = useCallback((evt = {}) => {
    const firstTouch = evt.touches[0];
    setXDown(firstTouch.clientX);
  }, []);

  const handleTouchMove = useCallback((evt) => {
    if (!infoBarRef.current.contains(evt.target)) {
      return;
    }
    const xUp = evt.touches[0].clientX;
    const xDiff = xDown - xUp;
    if (Math.abs(xDiff) > 120 && (xDiff < 0)) {
      dispatch(ActionPagesUpdateState('hideInfoBar', true));
    }
  }, [xDown]);

  useEffect(() => {
    infoBarRef.current.addEventListener('touchstart', handleTouchStart, false);
    infoBarRef.current.addEventListener('touchmove', handleTouchMove, false);
    return () => {
      infoBarRef.current.removeEventListener('touchstart', handleTouchStart, false);
      infoBarRef.current.removeEventListener('touchmove', handleTouchMove, false);
    };
  }, [xDown]);

  return (
    <div
      className={cl('info-nav_wrap', {active: !hideInfoBar})}
      ref={infoBarRef}
    >
      <div className="info-nav_block info-nav_block-lang">
        <LanguageSelect />
      </div>
      {loggedIn && (
        <div className="info-nav_block">
          <div className="info-nav_item">
            <div className="info-nav_icon-wrap">
              <Link
                to={{
                  pathname: ROUTES.PROFILE_SETTINGS.route,
                  hash: '#plan',
                }}
                alt={tariffName}
                title={tariffName}
                className={cl('info-nav_icon', {
                  'pay-status': !currentTariffDetails.default && user.tariff,
                  'not-pay': currentTariffDetails.default || !user.tariff,
                })}
              />
            </div>
            <div className="info-nav_txt">
              <Link to={buildRoute(URL_MAP.PLANS)} className="info-nav_txt link">
                {t('nav.tariffs')}
              </Link>
            </div>
          </div>
          <div className="info-nav_item">
            <Link to={ROUTES.CART.route} className="info-nav_item-hidden-link" />
            <div className="info-nav_icon-wrap">
              <div className="info-nav_icon card">
                {!!userCartData.length && (<span className="cart-count">{userCartData.length}</span>)}
              </div>
            </div>
            <span className="info-nav_txt middle">
              {!userCartData.length && (t('nav.emptyCart'))}
              {!!userCartData.length && (`${t('nav.servicesCart')}: ${userCartData.length}`)}
            </span>
          </div>
          <div className="info-nav_item">
            <div className="info-nav_icon-wrap">
              <div className="info-nav_icon tools" />
            </div>
            <Link to={ROUTES.PROFILE_SETTINGS.route} className="info-nav_txt">
              {t('nav.cabinet')}
            </Link>
          </div>
        </div>
      )}
      <div className="info-nav_block">
        <div className="info-nav_accordion">
          <HeaderAccordionMenu title={t('nav.forJobSeekers')} data={candidateLinks} />
          <HeaderAccordionMenu title={t('nav.forEmployers')} data={employerLinks} />
          <HeaderAccordionMenu title={t('nav.forAgency')} data={agencyLinks} />
          <HeaderAccordionMenu title={t('nav.mnj')} data={companyLinks} />
        </div>
      </div>
      <div className="info-nav_block">
        <div className="info-nav_link-list">
          {infoNavLinks.map(({link, title}, index) => (
            <Link to={link} className="info-nav_link" key={index}>
              {t(title)}
            </Link>
          ))}
        </div>
      </div>
      <div className="info-nav_block">
        <a href="https://navigator.sk.ru/orn/1123797" className="skolkovo-btn" target="_blank">
          <span className="icon icon-skolkovo" />
          {t('nav.skolkovo')}
        </a>
        <div className="info-nav_copy">
          © My new job,
          &nbsp;
          {new Date().getFullYear()}
          . {t('nav.rights')}
        </div>
      </div>
    </div>
  );
}

HeaderInfo.defaultProps = {
  hideInfoBar: true,
  loggedIn: false,
  cartData: {},
  user: {},
  router: {},
  language: 'ru',
};

HeaderInfo.propTypes = {
  hideInfoBar: PropTypes.bool,
  loggedIn: PropTypes.bool,
  cartData: PropTypes.shape({}),
  user: PropTypes.shape({}),
  router: PropTypes.shape({}),
  language: PropTypes.string,
};

function mapStateToProps({
  rSession, rPages, rCart, rUser,
}) {
  return {
    hideInfoBar: rPages.hideInfoBar,
    cartData: rCart.cartData,
    user: rUser,
    loggedIn: rSession.LOGGED_IN,
  };
}

export default connect(mapStateToProps, null)(memo(HeaderInfo));
